import {
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  MenuItem,
  Autocomplete,
  TextField,
  TableBody,
} from '@mui/material';
import { TextEditor, SelectEditor } from './styles';
import { Controller } from 'react-hook-form';
import { DataBox } from '../DataBox/DataBox';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parse, isValid, format } from 'date-fns';
import Switch from '../Switch/Switch';
import { formatDateDisplay } from '../../Utils/format-date';
import { AttachmentIcons } from '../AttachmentIcons/AttachmentIcons';

interface Props {
  title: string;
  fields: Field[];
  values: any;
  onEdit?: boolean;
  handleEdit?: () => void;
  control?: any;
  handleSave?: () => void;
  handleCancel?: () => void;
  handleAttach?: () => void;
  errors?: any;
  fullWidth?: boolean;
}

export const DataBoxTable = ({
  title,
  fields,
  values,
  onEdit,
  handleEdit,
  control,
  handleSave,
  handleCancel,
  handleAttach,
  errors,
  fullWidth,
}: Props) => {
  return (
    <DataBox
      title={title}
      handleEdit={handleEdit}
      handleCancel={handleCancel}
      handleSave={handleSave}
      handleAttach={handleAttach}
      onEdit={onEdit}
      fullWidth={fullWidth}
    >
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {fields?.map((detail, index) => (
              <TableRow key={detail.key || `row-${index}`} style={{ height: '55px' }}>
                <TableCell component="th" scope="row" style={{ fontWeight: 'bold' }}>
                  {detail.label}
                </TableCell>
                <TableCell style={{ fontSize: 15, padding: 5, width: '50%' }}>
                  {onEdit && detail.edit ? (
                    <Controller
                      name={detail.key}
                      control={control}
                      defaultValue={values[detail.key] ? values[detail.key] : 'undefined'}
                      render={({ field }) => {
                        if (detail.type === 'select' && detail.options) {
                          return (
                            <SelectEditor
                              {...field}
                              variant="outlined"
                              fullWidth
                              error={!!errors[detail.key]}
                            >
                              {detail.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </SelectEditor>
                          );
                        } else if (detail.type === 'text') {
                          return (
                            <TextEditor
                              {...field}
                              type={detail.textFieldType ?? 'text'}
                              variant="outlined"
                              fullWidth
                              error={!!errors[detail.key]}
                              helperText={errors[detail.key]?.message}
                              onChange={(e) => {
                                if (detail.textFieldType === 'number') {
                                  const value = parseInt(e.target.value);
                                  if (value < 0) {
                                    e.target.value = '0';
                                  }
                                }
                                field.onChange(e);
                              }}
                            />
                          );
                        } else if (detail.type === 'boolean') {
                          return (
                            <Switch
                              {...field}
                              checked={field.value || false}
                              onChange={field.onChange}
                            />
                          );
                        } else if (detail.type === 'autocomplete' && detail.options) {
                          const sortedOptions = [...detail.options].sort((a, b) =>
                            (a.name ?? '').localeCompare(b.name ?? ''),
                          );
                          return (
                            <Autocomplete
                              id={`${detail.key}-autocomplete`}
                              options={sortedOptions}
                              getOptionLabel={(option) => option.name ?? ''}
                              isOptionEqualToValue={(option, value) => option.id === value?.id}
                              onChange={(_, newValue) => {
                                field.onChange(newValue || '');
                              }}
                              value={field.value ?? ''}
                              slotProps={{
                                listbox: {
                                  style: { maxHeight: 200 },
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!errors[detail.key]}
                                  placeholder={detail.label}
                                  fullWidth
                                />
                              )}
                            />
                          );
                        } else {
                          const dateValue =
                            typeof field.value === 'string' && field.value
                              ? parse(field.value, 'yyyy-MM-dd', new Date())
                              : field.value || null;

                          return (
                            <DatePicker
                              sx={{ width: '100%' }}
                              {...field}
                              format="dd/MM/yyyy"
                              value={isValid(dateValue) ? dateValue : null}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? format(newValue, 'yyyy-MM-dd')
                                  : '';
                                field.onChange(formattedDate);
                              }}
                            />
                          );
                        }
                      }}
                    />
                  ) : (
                    (() => {
                      if (detail.type === 'boolean') {
                        const value = values[detail?.key];
                        return value ? 'Yes' : 'No';
                      } else if (detail.type === 'date') {
                        // Use the formatDateDisplay pipe for dates
                        return formatDateDisplay(values[detail?.key]);
                      } else if (detail.type === 'attachments') {
                        // Display attachments as icons
                        return <AttachmentIcons files={values[detail?.key] || []} maxVisible={4} />;
                      } else if (
                        typeof values[detail?.key] === 'object' &&
                        values[detail?.key] !== null
                      ) {
                        return values[detail?.key].name;
                      } else {
                        return values[detail?.key] || '-';
                      }
                    })()
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </DataBox>
  );
};
